import { useEffect, useState } from "react";
import { onIdTokenChanged } from "firebase/auth";
import { auth } from "../api/firebaseApi";

export type ImpersonationClaims = {
  impersonationId: string;
  impersonatorUid: string;
  impersonatorEmail?: string | null;
};

const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return v || null;
};

export const useImpersonationClaims = () => {
  const [claims, setClaims] = useState<ImpersonationClaims | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setClaims(null);
        setIsLoading(false);
        return;
      }

      try {
        const result = await user.getIdTokenResult();
        const rawClaims = result.claims as Record<string, unknown> & {
          impersonation_id?: unknown;
          impersonator_uid?: unknown;
          impersonator_email?: unknown;
        };
        const impersonationId = asNonEmptyString(rawClaims.impersonation_id);
        const impersonatorUid = asNonEmptyString(rawClaims.impersonator_uid);
        const impersonatorEmail = asNonEmptyString(
          rawClaims.impersonator_email
        );

        if (impersonationId && impersonatorUid) {
          setClaims({ impersonationId, impersonatorUid, impersonatorEmail });
        } else {
          setClaims(null);
        }
      } catch {
        setClaims(null);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return {
    isLoading,
    claims,
    isImpersonating: Boolean(claims)
  };
};
